import { Card } from '@nike/eds'
import { useGetFlagQuery } from 'api/feature-toggle'
import { dispatch } from 'api/redux'
import { fetchPrintConfig } from 'api/redux/store/application.slices'
import { LoadingOverlay } from 'components/loading-overlay'
import { WarningModal } from 'components/warning-modal'
import { type ReactElement } from 'react'
import { Screen } from 'types'
import { isBrimley, isFlight } from 'utils/dcContext'

import { CgradeCompletion } from './cgrade-completion'
import { Grading } from './grading'
import { Rework } from './rework'
import { ScanRlpnRasn } from './rlpn-rasn'
import { ScanTote } from './scan-tote'
import { ScanUnit } from './scan-unit'
import './UnitHandling.css'
import { useUnitHandlingApi } from './useUnitHandlingApi'

export const UnitHandling = () => {
  // Grade properties are now fetched with division in Grading.tsx
  dispatch(fetchPrintConfig())
  const {
    unitData,
    resetUnitDataToScanned,
    rasnData,
    rasnFamilyData,
    goodsHolder,
    goodsHolderTrackingId,
    activeScreen,
    setActiveScreen,
    processedQuantity,
    openRlpnContext,
    createRasnContext,
    createFamilyRasnContext,
    createUnitContext,
    createUnitBySkuContext,
    updateUnitGradeContext,
    updateUnitToteScanContext,
    printLabelContext,
    confirmCgradeComplete,
    closeGoodsHolderContext,
    resetUnitHandlingFlow,
    resetToScanRlpnRasn,
    displayFailure,
    gradeInstruction,
    isInstructionsLoading,
    warning,
    setWarning
  } = useUnitHandlingApi()

  const { data: canUseHash } = useGetFlagQuery('hash')
  const { data: canUseBgrade } = useGetFlagQuery('b_grade')
  const { data: weightsAndMeasuresRequiredFlag } = useGetFlagQuery('weights_and_measures_validation')
  const { data: showReturnsEndDate } = useGetFlagQuery('validate_digital_return_window_days')
  const { data: showMheConveyableWarning } = useGetFlagQuery('mhe_conveyable_flag')

  const screens: { [key in Screen]: ReactElement } = {
    [Screen.SCAN_RLPN_RASN]: (
      <ScanRlpnRasn
        openRlpnContext={openRlpnContext}
        createRasnContext={createRasnContext}
        createFamilyRasnContext={createFamilyRasnContext}
      />
    ),
    [Screen.SCAN_UNIT]: (
      <ScanUnit
        goodsHolder={goodsHolder}
        goodsHolderTrackingId={goodsHolderTrackingId}
        rasnData={rasnData!}
        rasnFamilyData={rasnFamilyData}
        processedQuantity={processedQuantity}
        createUnitContext={createUnitContext}
        createUnitBySkuContext={createUnitBySkuContext}
        finishRasnContext={resetToScanRlpnRasn}
        closeGoodsHolderContext={closeGoodsHolderContext}
        handlePauseCarton={resetToScanRlpnRasn}
        showReturnsEndDate={showReturnsEndDate!}
      />
    ),
    [Screen.GRADING]: (
      <Grading
        unitData={unitData}
        rasnChannel={rasnFamilyData?.channel ?? rasnData?.channel}
        handleReturn={() => {
          resetUnitHandlingFlow()
        }}
        updateUnitGradeContext={updateUnitGradeContext}
        useHash={canUseHash ?? false}
        useBgrade={canUseBgrade ?? false}
        useSgrade={isFlight ?? false}
        showMheConveyableWarning={showMheConveyableWarning ?? false}
        instruction={gradeInstruction}
      />
    ),
    [Screen.REWORK]: (
      <Rework
        unitData={unitData}
        handleReturn={() => {
          resetUnitDataToScanned()
          setActiveScreen(Screen.GRADING)
        }}
        handleContinue={() => {
          setActiveScreen(Screen.SCAN_TOTE)
        }}
        printLabelContext={printLabelContext}
        weightsAndMeasuresRequired={weightsAndMeasuresRequiredFlag ?? false}
      />
    ),
    [Screen.SCAN_TOTE]: (
      <ScanTote
        unitData={unitData}
        handleReturn={() => {
          setActiveScreen(isBrimley || unitData.grade === 'C_GRADE' ? Screen.GRADING : Screen.REWORK)
        }}
        updateUnitToteScanContext={updateUnitToteScanContext}
      />
    ),
    [Screen.CGRADE_COMPLETION]: (
      <CgradeCompletion
        unitData={unitData}
        handleReturn={() => {
          resetUnitDataToScanned()
          setActiveScreen(Screen.GRADING)
        }}
        updateUnitCgradeCompleteContext={confirmCgradeComplete}
        printLabelContext={printLabelContext}
      />
    )
  }

  return (
    <>
      <LoadingOverlay
        isLoading={
          openRlpnContext.showLoadingOverlay ||
          createRasnContext.showLoadingOverlay ||
          createUnitContext.showLoadingOverlay ||
          updateUnitGradeContext.showLoadingOverlay ||
          updateUnitToteScanContext.showLoadingOverlay ||
          closeGoodsHolderContext.showLoadingOverlay ||
          isInstructionsLoading
        }
      />
      {displayFailure && <div id="modal_failure" className="modal_failure" />}
      <WarningModal
        warning={warning}
        setWarning={setWarning} />
      <Card className="alignMiddle">
        {screens[activeScreen]}
      </Card>
    </>
  )
}
